import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  AfterContentInit,
  ViewEncapsulation,
  forwardRef,
  inject,
  input,
  linkedSignal,
  signal,
  computed
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
    selector: 'app-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CheckboxComponent),
            multi: true
        }
    ],
    imports: [FormsModule]
})
export class CheckboxComponent implements ControlValueAccessor, AfterContentInit {
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  label = input<string>('');
  disabled = input(false);
  value = input<boolean>(false);
  variant = input<'default' | 'error' | 'boundarycondition'>('default');
  /** `sm` draws the 16px box, for checkboxes inside list rows. */
  size = input<'sm' | 'md'>('md');
  /** Accessible name when there is no `label` and outside text cannot point at the box (it sits in a shadow root). */
  ariaLabel = input<string | null>(null);

  /**
   * Marks the box invalid (e.g. a required consent). Left null, a bound form
   * control decides: invalid once it has been touched.
   */
  invalid = input<boolean | null>(null);

  /** Internal disabled state that can be toggled by CVA setDisabledState */
  _disabledByCva = signal(false);

  /** Effective disabled state */
  isDisabled = computed(() => this.disabled() || this._disabledByCva());

  private readonly controlInvalid = signal(false);
  readonly isInvalid = computed(() => !this.isDisabled() && (this.invalid() ?? this.controlInvalid()));

  // After the node's own directives ran: formControlName links its control in ngOnChanges.
  ngAfterContentInit(): void {
    const control = this.injector.get(NgControl, null, { self: true, optional: true })?.control;
    if (!control) return;
    const sync = (): void => this.controlInvalid.set(control.invalid && control.touched);
    sync();
    const sub = control.events.subscribe(() => {
      sync();
      this.cdr.markForCheck();
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /**
   * Writable signal linked to the `value` input.
   * Follows parent changes but can be independently set by writeValue() (CVA).
   */
  private _value = linkedSignal<boolean, boolean>({
    source: this.value,
    computation: (src) => src || false
  });
  private onChange = (value: boolean) => {};
  private onTouched = () => {};

  get internalValue(): boolean {
    return this._value();
  }

  set internalValue(val: boolean) {
    if (val !== this._value()) {
      this._value.set(val);
      this.onChange(val);
      this.cdr.detectChanges();
    }
  }

  writeValue(value: boolean): void {
    this._value.set(value || false);
    this.cdr.detectChanges();
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByCva.set(isDisabled);
    this.cdr.detectChanges();
  }

  onCheckboxChange(e: Event): void {
    const target = e.target as HTMLInputElement;
    this.internalValue = target.checked;
  }

  onBlur(): void {
    this.onTouched();
  }

  getSanitizedLabel(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.label());
  }
}
